"use client";

import {useEffect, useRef} from "react";
import type {WorkflowEditorBinding} from "@/contexts/chat/WorkflowEditorBridgeContext";
import type {ConversationMessage} from "@/services/wingman";
import {confirmedDraftSaves} from "@/services/wingman/omega-results";
import {confirmedWorkflowPublications} from "./workflowPublicationReceipt";

/**
 * Keeps the builder beside this chat in step with Wingman: the canvas is
 * read-only while a turn runs, and reloads when a turn saves or publishes its workflow.
 */
export function useWorkflowEditorSync({
   binding,
   conversationId,
   messages,
   historyLoaded,
   busy,
}: {
   binding: WorkflowEditorBinding | null;
   conversationId: string;
   messages: ConversationMessage[];
   historyLoaded: boolean;
   busy: boolean;
}) {
   useEffect(() => {
      if (!binding || !busy) return;
      return binding.holdCanvas();
   }, [binding, busy]);

   // Writes already in the loaded history are not news; only writes that arrive
   // after it are reloaded into the canvas.
   const seenWritesRef = useRef<Set<string> | null>(null);
   useEffect(() => {
      seenWritesRef.current = null;
   }, [conversationId]);

   useEffect(() => {
      if (!historyLoaded) return;
      const writes = [...confirmedDraftSaves(messages), ...confirmedWorkflowPublications(messages)];
      if (seenWritesRef.current === null) {
         seenWritesRef.current = new Set(writes.map((write) => write.callId));
         return;
      }
      const seen = seenWritesRef.current;
      const fresh = writes.filter((write) => !seen.has(write.callId));
      fresh.forEach((write) => seen.add(write.callId));
      // The builder owns a separate canvas hold until this reload finishes.
      if (binding && fresh.some((write) => write.workflow.id === binding.workflowId)) void binding.onDraftSaved();
   }, [binding, historyLoaded, messages]);
}
